import { Link } from "react-router-dom";
import { CASES } from "../data/portfolio";
import { FULL_STACK, StackLogo } from "../components/StackLogo";
import { IntakeBookingFlow } from "../components/IntakeBookingFlow";
import { FooterContacts, IconMeta, IconWhatsApp } from "../components/FooterContacts";
import { LinearHeroMockup } from "../components/LinearHeroMockup";
import { LinearServicesBento } from "../components/LinearServicesBento";
import { LinearMethod } from "../components/LinearMethod";

import { RepoCatalog } from "../components/RepoCatalog";

const STACK_ROW_A = FULL_STACK.filter((_, i) => i % 2 === 0);
const STACK_ROW_B = FULL_STACK.filter((_, i) => i % 2 === 1);

export function Home() {
  return (
    <div className="linear-page">
      {/* Precision Sticky Navigation */}
      <nav className="linear-nav">
        <div className="linear-nav-inner">
          <Link to="/" className="linear-brand" aria-label="Automatrix Home">
            <span className="linear-brand-symbol">A</span>
            <span className="linear-brand-name">AUTOMATRIX</span>
          </Link>

          <div className="linear-nav-links">
            <a href="#services">Services</a>
            <a href="#method">Engagements</a>
            <a href="#systems">Selected Cases</a>
            <a href="#repos">168 Repos</a>
            <a href="#stack">Tech Stack</a>
          </div>

          <div className="linear-nav-actions">
            <div className="nav-meta-badge" title="Official Meta Authenticated Technology Provider">
              <IconMeta size={13} />
              <span className="nav-meta-text">Meta Provider</span>
            </div>

            <a
              href="#book-call"
              className="linear-btn-primary nav-cta"
            >
              <span>Book Call</span>
              <span className="cta-arrow">→</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section — High-Impact Value Proposition & Live Telemetry Mockup */}
      <section className="linear-hero-section">
        <div className="linear-container">
          <div className="linear-hero-center">
            {/* Announcement / Status Pill */}
            <div className="linear-kicker-pill">
              <span className="kicker-pulse-dot" />
              <span className="kicker-meta-icon">
                <IconMeta size={13} />
              </span>
              <span className="kicker-text">Meta Authenticated Technology Provider</span>
              <span className="kicker-sep">·</span>
              <span className="kicker-tz">02:00 – 18:00 GMT-3</span>
            </div>

            {/* Monumental Linear Headline */}
            <h1 className="linear-headline">
              Enterprise AI Systems.
              <span className="linear-headline-gradient">
                Engineered for production scale.
              </span>
            </h1>

            {/* Subtitle with High-Ticket Service Focus */}
            <p className="linear-subhead">
              We design, harden, and deploy autonomous multi-agent networks, deep Meta Cloud API
              pipelines, and deterministic AI infrastructure for companies where failure is not an option.
            </p>

            {/* CTAs & Direct Contact */}
            <div className="linear-cta-row">
              <a href="#book-call" className="linear-btn-primary">
                <span>Schedule Architecture Consultation</span>
                <span className="cta-arrow">→</span>
              </a>

              <a href="#services" className="linear-btn-secondary">
                <span>Explore Core Capabilities</span>
                <span className="cta-arrow">↓</span>
              </a>
            </div>

            {/* Quick Contact & WhatsApp Bar */}
            <div className="linear-hero-direct">
              <span className="direct-label">Direct Executive WhatsApp:</span>
              <a
                href="https://wa.me/5521988407505?text=Hello%20Lucas!%20I%20would%20like%20to%20discuss%20an%20AI%20systems%20architecture."
                target="_blank"
                rel="noopener noreferrer"
                className="direct-link"
              >
                <IconWhatsApp size={13} />
                <span>+55 21 98840-7505</span>
              </a>
              <span className="direct-sep">·</span>
              <a
                href="https://wa.me/5521974445234?text=Hello!%20I%20would%20like%20information%20regarding%20Automatrix%20solutions."
                target="_blank"
                rel="noopener noreferrer"
                className="direct-link"
              >
                <IconWhatsApp size={13} />
                <span>+55 21 97444-5234</span>
              </a>
            </div>
          </div>

          {/* Hero Visual Mockup: Live Agent Telemetry Console */}
          <div className="hero-mockup-container">
            <LinearHeroMockup />
          </div>
        </div>
      </section>

      {/* Social Proof Ribbon / Industry Ecosystem */}
      <section className="linear-ecosystem-ribbon">
        <div className="linear-container">
          <p className="ribbon-label">
            ENGINEERED ON INDUSTRY-GRADE PLATFORMS &amp; FRONTIER MODEL PIPELINES
          </p>
          <div className="ribbon-logos">
            <span className="ribbon-logo-item">Meta Cloud API</span>
            <span className="ribbon-logo-item">GoHighLevel</span>
            <span className="ribbon-logo-item">Anthropic Claude</span>
            <span className="ribbon-logo-item">OpenAI GPT-4o</span>
            <span className="ribbon-logo-item">Supabase Postgres</span>
            <span className="ribbon-logo-item">n8n Engine</span>
            <span className="ribbon-logo-item">Stanford Research</span>
          </div>
        </div>
      </section>

      {/* Section 02: Core AI Engineering Services (Bento Grid) */}
      <section id="services" className="linear-section">
        <div className="linear-container">
          <div className="section-head">
            <span className="section-kicker">SERVICES &amp; CAPABILITIES</span>
            <h2 className="section-title">End-to-end AI systems engineering.</h2>
            <p className="section-desc">
              We bridge the gap between fragile API wrappers and production-grade autonomous infrastructure.
            </p>
          </div>

          <LinearServicesBento />
        </div>
      </section>

      {/* Section 03: Delivery Method & Engagement Tiers */}
      <section id="method" className="linear-section alt-bg">
        <div className="linear-container">
          <div className="section-head">
            <span className="section-kicker">THE AUTOMATRIX METHOD</span>
            <h2 className="section-title">How we deliver mission-critical software.</h2>
            <p className="section-desc">
              Structured sprints, deterministic safety benchmarks, and continuous operational observability.
            </p>
          </div>

          <LinearMethod />
        </div>
      </section>

      {/* Section 04: Production Systems (Selected Enterprise Cases) */}
      <section id="systems" className="linear-section">
        <div className="linear-container">
          <div className="section-head">
            <span className="section-kicker">PRODUCTION EVIDENCE</span>
            <h2 className="section-title">Tested in real enterprise environments.</h2>
            <p className="section-desc">
              High-volume hiring, executive business communications, and autonomous swarm command centers.
            </p>
          </div>

          <div className="systems-grid">
            {CASES.map((c) => (
              <div className="system-card" key={c.slug}>
                <div
                  className="system-card-viewport"
                  style={
                    c.thumbnail
                      ? { backgroundImage: `linear-gradient(180deg, rgba(8,9,10,0.1) 0%, rgba(8,9,10,0.9) 100%), url(${c.thumbnail})` }
                      : { background: c.accent }
                  }
                >
                  <div className="system-card-badges">
                    <span className="system-cat">{c.category}</span>
                    {c.region && <span className="system-reg">{c.region}</span>}
                  </div>
                </div>

                <div className="system-card-body">
                  {c.client && <span className="system-client">{c.client}</span>}
                  <h3 className="system-title">{c.title}</h3>
                  <p className="system-desc">{c.tagline}</p>

                  <div className="system-highlights-list">
                    {c.highlights.slice(0, 2).map((h, i) => (
                      <div className="highlight-row" key={i}>
                        <span className="highlight-dot">✓</span>
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>

                  <div className="system-stack-chips">
                    {c.stack.slice(0, 4).map((tech) => (
                      <span className="stack-chip" key={tech}>{tech}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 05: Tech Stack Marquee */}
      <section id="stack" className="linear-section alt-bg">
        <div className="linear-container">
          <div className="section-head center">
            <span className="section-kicker">TECHNOLOGY ECOSYSTEM</span>
            <h2 className="section-title">Architected on the modern AI stack.</h2>
          </div>

          <div className="pf-stack-marquee">
            <div className="pf-stack-track-wrap">
              <div className="pf-stack-track">
                {[...STACK_ROW_A, ...STACK_ROW_A].map((entry, i) => (
                  <StackLogo entry={entry} key={`${entry.name}-${i}`} />
                ))}
              </div>
            </div>
            <div className="pf-stack-track-wrap">
              <div className="pf-stack-track pf-stack-track-reverse">
                {[...STACK_ROW_B, ...STACK_ROW_B].map((entry, i) => (
                  <StackLogo entry={entry} key={`${entry.name}-${i}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 06: 168 Repositories Catalog */}
      <section id="repos" className="linear-section">
        <div className="linear-container">
          <div className="section-head">
            <span className="section-kicker">ENGINEERING ARTIFACTS</span>
            <h2 className="section-title">168 Production Repositories Catalog.</h2>
            <p className="section-desc">
              Full architectural inventory authored by Lucas Alves (Morfeu333) — autonomous agents, CRM middleware, and edge cloud systems.
            </p>
          </div>

          <RepoCatalog />
        </div>
      </section>

      {/* Section 07: Dedicated Booking / Consultation Section */}
      <section id="book-call" className="linear-booking-section">
        <div className="linear-container">
          <div className="booking-split-grid">
            {/* Left: Consultation Scope & Expectations */}
            <div className="booking-context-panel">
              <span className="section-kicker">SCHEDULE CONSULTATION</span>
              <h2 className="booking-heading">
                Book a direct technical architecture call.
              </h2>
              <p className="booking-lead">
                Every consultation is conducted directly with <strong>Lucas Alves</strong>, founder and lead architect at Automatrix (18 years in technology, independent research in Silicon Valley).
              </p>

              <div className="consultation-features">
                <div className="feature-item">
                  <span className="feature-icon">🔍</span>
                  <div>
                    <strong>Architecture &amp; Feasibility Scoping</strong>
                    <p>Review your tech stack, APIs, data readiness, and realistic agent capability bounds.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">🛡️</span>
                  <div>
                    <strong>Deterministic Security &amp; Evals</strong>
                    <p>Identify hallucination risks, schema validation requirements, and human approval gates.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">⚡</span>
                  <div>
                    <strong>Sprint Timeline &amp; Cost Estimation</strong>
                    <p>Receive a clear execution roadmap with fixed deliverables and transparent engagement options.</p>
                  </div>
                </div>
              </div>

              {/* Operating Timezone & WhatsApp Support */}
              <div className="booking-tz-badge">
                <div className="tz-row">
                  <span className="tz-label">Operating Schedule:</span>
                  <span className="tz-val">02:00 – 18:00 GMT-3 (Global Availability)</span>
                </div>
                <div className="tz-row">
                  <span className="tz-label">Emergency Executive Contact:</span>
                  <a href="https://wa.me/5521988407505" className="tz-link" target="_blank" rel="noreferrer">
                    +55 21 98840-7505
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Seamless Fluid Booking Flow Container */}
            <div className="booking-widget-panel">
              <div className="booking-widget-card">
                <IntakeBookingFlow />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Linear-Grade Footer */}
      <footer className="linear-footer">
        <div className="linear-container">
          <div className="footer-top-row">
            <div>
              <span className="footer-brand">AUTOMATRIX IA</span>
              <p className="footer-lead">
                Enterprise AI Systems Architecture · Autonomous Multi-Agent Swarms · Meta Authenticated Provider
              </p>
            </div>
            <div className="footer-tz-block">
              <span className="footer-tz-label">OPERATING WINDOW</span>
              <span className="footer-tz-val">02:00 – 18:00 GMT-3</span>
            </div>
          </div>

          <FooterContacts />
        </div>
      </footer>
    </div>
  );
}
